/* CoursesDashboard.jsx */
import { useState, useEffect, useMemo } from "react";
import {
  Grid,
  CircularProgress,
  Box,
  Divider,
} from "@mui/material";

import PeriodSelect from "./PeriodSelect";
import crudsService from "services/cruds-service";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import DefaultDoughnutChart from "examples/Charts/DoughnutCharts/DefaultDoughnutChart";
import DataTable from "examples/Tables/DataTable";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";

/* helpers */
const msToDay = ms => Math.round(ms / 86_400_000);
const pickColors = n => {
  const palette = [
    "info", "dark", "error", "secondary", "primary", "warning", "success",
  ];
  return Array.from({ length: n }, (_, i) => palette[i % palette.length]);
};

export default function CoursesDashboard() {
  const [period, setPeriod] = useState("yesterday");
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setBusy(true);
    crudsService.getAggDaily(period)
      .then(r => { setDoc(r.data[0].course_metrics); })
      .finally(() => setBusy(false));
  }, [period]);

  /* line-series for enrol/completion/cert */
  const enrolLine = useMemo(() => ({
    labels: (doc?.timeseries?.enrolments_per_day || []).map(p => p.x),
    datasets: {
      label: "Enrolments", color: "info",
      data: (doc?.timeseries?.enrolments_per_day || []).map(p => p.y)
    }
  }), [doc]);
  const compLine = useMemo(() => ({
    labels: (doc?.timeseries?.completions_per_day || []).map(p => p.x),
    datasets: {
      label: "Completions", color: "success",
      data: (doc?.timeseries?.completions_per_day || []).map(p => p.y)
    }
  }), [doc]);
  const certLine = useMemo(() => ({
    labels: (doc?.timeseries?.certificates_per_day || []).map(p => p.x),
    datasets: {
      label: "Certificates", color: "warning",
      data: (doc?.timeseries?.certificates_per_day || []).map(p => p.y)
    }
  }), [doc]);

  /* assessments per hour stacked bar */
  const assessBar = useMemo(() => {
    const start = doc?.timeseries?.assessment_started_per_hour || Array(24).fill(0);
    const sub = doc?.timeseries?.assessment_submitted_per_hour || Array(24).fill(0);
    return {
      labels: start.map((_, h) => {
        const t = new Date(0, 0, 0, h, 0, 0);
        return `${t.toLocaleString("en-US", { hour: "numeric", hour12: true })}`;
      }),
      datasets: [
        { label: "Started", color: "error", data: start },
        { label: "Submitted", color: "success", data: sub },
      ]
    };
  }, [doc]);

  if (busy || !doc)
    return (<Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>);

  const { kpi, pie, extras, generated_at } = doc;

  return (
    <>
      <MDBox mb={2}>
        {/* <PeriodSelect value={period} onChange={setPeriod} /> */}
      </MDBox>

      <MDTypography variant="h4">Headline KPIs</MDTypography>
      <MDTypography variant="button">
        Data generated on <strong>{new Date(generated_at).toLocaleDateString()}</strong>
      </MDTypography>

      <Grid container spacing={3} mt={1}>
        <Grid item xs={12} md={3}>
          <MiniStatisticsCard title={{ text: "Total Courses Published" }} count={kpi.total_courses} icon={{ component: "school", color: "primary" }} />
        </Grid>
        <Grid item xs={12} md={3}>
          <MiniStatisticsCard title={{ text: "Enrolments today" }} count={kpi.enrolments_today} icon={{ component: "insights", color: "info" }} />
        </Grid>
        <Grid item xs={12} md={3}>
          <MiniStatisticsCard title={{ text: "Course Completions today" }} count={kpi.completions_today} icon={{ component: "check_circle", color: "success" }} />
        </Grid>
        <Grid item xs={12} md={3}>
          <MiniStatisticsCard title={{ text: "Course Certificates today" }} count={kpi.certificates_today} icon={{ component: "military_tech", color: "warning" }} />
        </Grid>
        <Grid item xs={12} md={3}>
          <MiniStatisticsCard title={{ text: "Avg completion %" }} count={`${kpi.avg_completion_rate_pct}%`} icon={{ component: "percent", color: "secondary" }} />
        </Grid>
        <Grid item xs={12} md={3}>
          <MiniStatisticsCard title={{ text: "Avg time-to-complete (d)" }} count={msToDay(kpi.avg_time_to_complete_ms)} icon={{ component: "schedule", color: "dark" }} />
        </Grid>
      </Grid>

      {/* daily line charts */}
      <Divider />
      <MDTypography variant="h4">Daily trends (30 d)</MDTypography>
      <Grid container spacing={3} mt={1}>
        <Grid item xs={12} md={4}>
          <ReportsLineChart color="info" title="Enrolments" chart={enrolLine} date={`updated ${period.replace("_", " ")}`} />
        </Grid>
        <Grid item xs={12} md={4}>
          <ReportsLineChart color="success" title="Completions" chart={compLine} date={`updated ${period.replace("_", " ")}`} />
        </Grid>
        <Grid item xs={12} md={4}>
          <ReportsLineChart color="warning" title="Certificates" chart={certLine} date={`updated ${period.replace("_", " ")}`} />
        </Grid>
      </Grid>

      {/* assessments per hour */}
      <Divider />
      <MDTypography variant="h4" mb={4}>Assessments – yesterday</MDTypography>
      <VerticalBarChart
        
        icon={{ component: "bar_chart", color: "info" }}
        title="Started vs submitted per hour"
        description="UTC hours"
        chart={assessBar}
      />

      {/* doughnuts */}
      <Divider />
      <MDTypography variant="h4">Completion breakdown</MDTypography>
      <Grid container spacing={3} mt={1}>
        <Grid item xs={12} md={4}>
          <DefaultDoughnutChart
            title="Enrolments by course"
            chart={{
              labels: Object.keys(pie.enrolments_by_course),
              datasets: {
                label: "enrol", data: Object.values(pie.enrolments_by_course),
                backgroundColors: pickColors(Object.keys(pie.enrolments_by_course).length)
              }
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <DefaultDoughnutChart
            title="Completion status"
            chart={{
              labels: Object.keys(pie.completion_status),
              datasets: {
                label: "status", data: Object.values(pie.completion_status),
                backgroundColors: pickColors(2)
              }
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <DefaultDoughnutChart
            title="Pass vs Fail"
            chart={{
              labels: Object.keys(pie.pass_fail),
              datasets: {
                label: "result", data: Object.values(pie.pass_fail),
                backgroundColors: pickColors(2)
              }
            }}
          />
        </Grid>
      </Grid>

      {/* top courses table */}
      <Divider />
      <MDTypography variant="h4" mb={2}>Top courses (enrolments till date)</MDTypography>
      <DataTable
        table={{
          columns: [
            { Header: "course", accessor: "courseName", width: "60%" },
            { Header: "enrolments", accessor: "enrolled_users", width: "20%" },
            { Header: "Enrolment %", accessor: "enrolment_percent", width: "20%" },
          ],
          rows: extras.top_courses_enrolments.map(c => ({
            courseName: c.courseName || c.courseId,
            enrolled_users: c.enrolled_users,
            enrolment_percent: `${c.enrolment_percent}%`,
          })),
        }}
      />
    </>
  );
}
